import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Insights from '@mui/icons-material/Insights';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';

import { Alert } from '@app/ui/alert';
import { Label } from '@app/ui/label';
import { Tooltip } from '@app/ui/tooltip';

import { TIER_LABEL_SX } from '../constants';
import { MatchingTier } from '../types';
import { ResultsViewPair } from '../utils/build-results-view';
import { formatCalendarDate } from '../utils/format-calendar-date';
import { ProposedIcon } from './ProposedIcon';
import { ScoreJustificationModal } from './ScoreJustificationModal';
import { SentCue } from './SentCue';

interface PairRowProps {
  readonly pair: ResultsViewPair;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly sent: boolean;
  readonly onEmail: () => void;
}

// 15px and 22px have no typography token (`body1` 14, `subtitle1` 16) — design values, one-off
// (EVIDENCE §1.1/§1.2).
const COLUMN_HEADING_SX = { fontSize: 15, fontWeight: 600 };
const INDICATOR_SX = { fontSize: 22, display: 'inline-flex' };

const ExplanationColumn = ({ heading, lines }: { heading: string; lines: string[] }) => {
  const { t } = useTranslation('common');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 0 auto', width: 300 }}>
      <Typography sx={COLUMN_HEADING_SX}>{heading}</Typography>
      {lines.length ? (
        lines.map(line => (
          <Typography key={line} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {line}
          </Typography>
        ))
      ) : (
        <Typography variant="body1" color="text.secondary">
          {t('matching.results.none')}
        </Typography>
      )}
    </Box>
  );
};

export const PairRow = ({ pair, open, onToggle, sent, onEmail }: PairRowProps) => {
  const { t } = useTranslation('common');
  const [justificationOpen, setJustificationOpen] = useState(false);
  const hasHardFail = pair.hardFails.length > 0;
  const breakdownLink = (
    <Box
      component="button"
      type="button"
      onClick={() => setJustificationOpen(true)}
      sx={{
        flex: 'none',
        whiteSpace: 'nowrap',
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        font: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.4,
        color: 'primary.main',
        cursor: 'pointer',
        '&:hover': { color: 'primary.dark' },
      }}
    >
      <Insights sx={{ fontSize: 18 }} />
      {t('matching.justification.cta')}
    </Box>
  );

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'grey.500',
        py: 2,
        px: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <IconButton
          aria-label={t('matching.results.toggleDetails') ?? undefined}
          aria-expanded={open}
          onClick={onToggle}
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            ...(open
              ? { bgcolor: 'primary.main', color: 'accordionIconOpen.main' }
              : { bgcolor: 'primary.light', color: 'primary.main' }),
            '&:hover': { bgcolor: open ? 'primary.dark' : 'primary.light' },
          }}
        >
          {open ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
        </IconButton>

        <Typography variant="h4">{pair.displayName}</Typography>

        <Label
          text={t(`matching.groups.${pair.tier}`)}
          sx={{ height: 26, px: 2, ...(TIER_LABEL_SX[pair.tier] ?? TIER_LABEL_SX[MatchingTier.standard]) }}
        />

        {/* The Q5 ruling: a proposed pair MAY carry hard fails. Both render, neither suppresses the
            other, and no conditional may couple them. */}
        {pair.proposed && (
          <Label
            text={t('matching.results.proposed')}
            icon={<ProposedIcon />}
            sx={{ height: 26, px: 1.5, color: 'primary.main', bgcolor: 'primary.light' }}
          />
        )}

        {hasHardFail && (
          <Tooltip title={t('matching.results.hardFailTooltip')} color="inherit" iconSize="inherit">
            <ReportProblemOutlined sx={{ ...INDICATOR_SX, color: 'error.main' }} />
          </Tooltip>
        )}

        {pair.hasDataGap && (
          <Tooltip title={t('matching.results.dataGapTooltip')} color="inherit" iconSize="inherit">
            <ErrorOutline sx={{ ...INDICATOR_SX, color: 'warning.main' }} />
          </Tooltip>
        )}

        {/* Client ruling: the "already sent" cue is always the last marker in the row. */}
        {sent && <SentCue title={t('matching.results.sendCue')} />}

        {open && (
          <Typography variant="body1" color="text.secondary" sx={{ ml: 3 }}>
            {pair.startSurroSearch
              ? t('matching.results.availableSince', {
                  date: formatCalendarDate(pair.startSurroSearch),
                })
              : t('matching.results.noDate')}
          </Typography>
        )}

        {/* Right-hand flex slot — the send ticket's Email button and "already sent" cue drop in
            here as additive children (SPEC §4.9 constraint 1). */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Design `:242` — contained primary, and only while the row is expanded (Q-1). */}
          {open && (
            <Button variant="contained" color="primary" size="small" onClick={onEmail}>
              {t('matching.results.emailButton')}
            </Button>
          )}

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 78,
              height: 36,
              px: 2.25,
              borderRadius: 999,
              border: 1,
              borderColor: 'primary.main',
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {/* AC-4 — 02 delivers an integer; the FE appends the sign and never rounds. */}
            {`${pair.score}%`}
          </Box>
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 2.5, pb: 1, pl: 5 }}>
          {/* Hard fails ride ABOVE the columns as an error alert, as drawn (ruling 2026-09-08 over the
              SPEC's "third column"). Shown iff non-empty — never derived from the score. */}
          {/* With a hard fail the link moves up, under Email & % (client ruling 2026-09-24). */}
          {hasHardFail && <Box sx={{ alignSelf: 'flex-end', mt: -1, mb: -2 }}>{breakdownLink}</Box>}
          {hasHardFail && (
            <Alert severity="error" inner>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={COLUMN_HEADING_SX}>{t('matching.results.hardFails')}</Typography>
                {pair.hardFails.map(line => (
                  <Typography key={line} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          {/* The link sits outside the wrapping columns so it stays top-right instead of wrapping below them. */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
              <ExplanationColumn heading={t('matching.results.pros')} lines={pair.pros} />
              <ExplanationColumn heading={t('matching.results.cons')} lines={pair.cons} />
            </Box>

            {!hasHardFail && breakdownLink}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={COLUMN_HEADING_SX}>{t('matching.results.summary')}</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {pair.summary}
            </Typography>
          </Box>
        </Box>
      </Collapse>

      <ScoreJustificationModal
        open={justificationOpen}
        onClose={() => setJustificationOpen(false)}
        view={pair.justification}
      />
    </Box>
  );
};
